/**
 * DeviceAuditView
 * Scans all physical devices directly (local IP first) to find registered visitors.
 * Shows any visitor/service_provider found on any device — regardless of their DB status.
 * Allows bulk revocation directly from device data.
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  ArrowLeft,
  RefreshCw,
  CheckSquare,
  Square,
  User,
  ScanLine,
  ShieldOff,
  Wifi,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { condoApi } from '../../../services/api';
import {
  deviceCommunicationService,
  DeviceOperations,
  CommunicationMode,
} from '../../../services/deviceCommunication';
import { expiredVisitorDeviceService } from '../../../services/expiredVisitorDeviceService';
import { ExpiredVisitor } from '../../../hooks/useExpiredVisitors';
import { getAbsoluteImageUrl } from '../../../utils/imageUrl';
import { DeviceRemovalProgress } from './DeviceRemovalProgress';
import { isElectron } from '../../../utils/platform';
import toast from 'react-hot-toast';
import { useConfirm } from '../../ui/ConfirmDialog';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DeviceAuditEntry {
  /** Unique key = personId + '-' + join(deviceIds) */
  key: string;
  personId: string;
  name: string;
  photo: string | null;
  visitorState: string; // 'active' | 'expired' | 'exited' | 'unknown'
  roleType: string;
  deviceIds: string[];       // devices where this visitor was found
  devicePersonId: string;    // the employeeNo / registration on the device
  roleId?: string;
}

interface ScanProgress {
  current: number;
  total: number;
  deviceId: string;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

const stateLabel = (state: string) => {
  switch (state) {
    case 'active':   return { label: 'Ativo',    cls: 'bg-green-950 text-green-400' };
    case 'expired':  return { label: 'Expirado', cls: 'bg-red-950 text-red-400' };
    case 'exited':   return { label: 'Saiu',     cls: 'bg-gray-800 text-gray-400' };
    default:         return { label: 'Desconhecido', cls: 'bg-yellow-950 text-yellow-500' };
  }
};

/** Parse LOAD_PEOPLE raw response into { employeeNo, name }[] */
const parseDeviceUsers = (vendor: string, data: any): { employeeNo: string; name: string }[] => {
  if (vendor === 'hikvision') {
    const arr: any[] = data?.UserInfoSearch?.UserInfo || [];
    return arr.map(u => ({ employeeNo: String(u.employeeNo || ''), name: String(u.name || '') }));
  }
  if (vendor === 'controlid') {
    const arr: any[] = data?.users || [];
    return arr.map(u => ({ employeeNo: String(u.registration || u.id || ''), name: String(u.name || '') }));
  }
  const arr: any[] = data?.users || data?.UserInfo || [];
  return arr.map(u => ({ employeeNo: String(u.employeeNo || u.registration || u.id || ''), name: String(u.name || '') }));
};

// ─── Row ─────────────────────────────────────────────────────────────────────

const AuditRow: React.FC<{
  entry: DeviceAuditEntry;
  isSelected: boolean;
  onToggle: (key: string) => void;
  onPersonClick: (personId: string) => void;
}> = ({ entry, isSelected, onToggle, onPersonClick }) => {
  const [imgError, setImgError] = useState(false);
  const { label, cls } = stateLabel(entry.visitorState);

  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 border-b border-gray-800/60 hover:bg-gray-900/40 transition-colors ${isSelected ? 'bg-gray-900/60' : ''}`}>
      <button onClick={() => onToggle(entry.key)} className="flex-shrink-0 text-gray-500 hover:text-gray-300 transition-colors">
        {isSelected ? <CheckSquare className="w-4 h-4 text-red-500" /> : <Square className="w-4 h-4" />}
      </button>

      <div
        className="flex-shrink-0 w-9 h-9 rounded-full overflow-hidden bg-gray-800 flex items-center justify-center cursor-pointer"
        onClick={() => onPersonClick(entry.personId)}
      >
        {entry.photo && !imgError ? (
          <img src={getAbsoluteImageUrl(entry.photo)} alt={entry.name} className="w-full h-full object-cover" onError={() => setImgError(true)} />
        ) : (
          <User className="w-4 h-4 text-gray-500" />
        )}
      </div>

      <button onClick={() => onPersonClick(entry.personId)} className="flex-1 min-w-0 text-left">
        <span className="text-sm font-medium text-gray-200 truncate block hover:text-white transition-colors">
          {entry.name}
        </span>
      </button>

      {/* Devices found on */}
      <span className="flex-shrink-0 flex items-center gap-1 text-xs text-blue-400 bg-blue-950/30 px-1.5 py-0.5 rounded">
        <Wifi className="w-3 h-3" />
        {entry.deviceIds.length} dispos.
      </span>

      {/* Status */}
      <span className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>
        {label}
      </span>
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────

interface DeviceAuditViewProps {
  customerId: string;
  onBack: () => void;
  onPersonClick: (personId: string) => void;
}

export const DeviceAuditView: React.FC<DeviceAuditViewProps> = ({
  customerId,
  onBack,
  onPersonClick,
}) => {
  const confirm = useConfirm();
  const [scanState, setScanState] = useState<'idle' | 'scanning' | 'done'>('idle');
  const [scanProgress, setScanProgress] = useState<ScanProgress>({ current: 0, total: 0, deviceId: '' });
  const [entries, setEntries] = useState<DeviceAuditEntry[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [isRemoving, setIsRemoving] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [removalProgress, setRemovalProgress] = useState({ current: 0, total: 0, name: '' });
  const [scanError, setScanError] = useState<string | null>(null);

  // ── Scan ───────────────────────────────────────────────────────────────────

  const runScan = useCallback(async () => {
    setScanState('scanning');
    setScanProgress({ current: 0, total: 0, deviceId: '' });
    setEntries([]);
    setSelectedKeys(new Set());
    setScanError(null);

    try {
      // 1. Fetch all visitors from the API (active + inactive, all states)
      const response = await condoApi.getPeople(customerId, {
        role_type: 'visitor,service_provider',
        include_inactive_roles: true,
        page_size: 500,
      });
      const people: any[] = response.data?.people || response.data?.data || [];

      // 2. Build lookup: devicePersonId (lower) → visitor info
      const lookup = new Map<string, {
        personId: string;
        name: string;
        photo: string | null;
        visitorState: string;
        roleType: string;
        roleId: string;
        devicePersonId: string;
      }>();

      for (const person of people) {
        const fullName = `${person.first_name} ${person.last_name}`.trim();
        const photo = getAbsoluteImageUrl(person.identity_photo_url || person.profile_picture_url) || null;

        for (const role of person.roles || []) {
          if (role.role_type !== 'visitor' && role.role_type !== 'service_provider') continue;
          const dpId = role.role_metadata?.device_person_id;
          if (!dpId) continue;

          lookup.set(dpId.toLowerCase(), {
            personId: person.id,
            name: fullName,
            photo,
            visitorState: role.role_metadata?.visitor_state || 'active',
            roleType: role.role_type,
            roleId: role.id,
            devicePersonId: dpId,
          });
        }
      }

      // 3. Fetch all devices for this customer
      const { fetchWithAuth } = await import('../../../services/api');
      const { okJson } = await import('../../../services/httpError');
      const devResp = await fetchWithAuth(`/api/v1/iot-devices/customer/${customerId}`);
      // Unchecked, a rejected lookup left zero devices to scan and the audit reported a clean
      // result — the most misleading possible outcome for an audit.
      const devData = await okJson<any>(devResp, 'Erro ao carregar os dispositivos do cliente');
      const devices: any[] = (devData?.devices || devData?.data || []).map((d: any) => ({
        id: d.id,
        ip_address: d.ip_address,
        local_ip_address: d.local_ip_address,
        username: d.username || d.device_username || 'admin',
        password: d.password || d.device_password || '',
        vendor: d.vendor || 'hikvision',
        port: d.port,
      }));

      setScanProgress({ current: 0, total: devices.length, deviceId: '' });

      // 4. For each device, call LOAD_PEOPLE and cross-reference
      const found = new Map<string, DeviceAuditEntry>(); // personId → entry

      for (let i = 0; i < devices.length; i++) {
        const device = devices[i];
        setScanProgress({ current: i, total: devices.length, deviceId: device.id });

        try {
          const result = await deviceCommunicationService.executeOperation(
            device,
            DeviceOperations.LOAD_PEOPLE,
            {},
            isElectron() ? CommunicationMode.HYBRID : CommunicationMode.CLOUD
          );

          if (result.success && result.data) {
            const users = parseDeviceUsers(device.vendor, result.data);

            for (const u of users) {
              if (!u.employeeNo) continue;
              const info = lookup.get(u.employeeNo.toLowerCase());
              if (!info) continue; // not a known visitor → skip

              if (found.has(info.personId)) {
                // Already found on another device — append deviceId
                found.get(info.personId)!.deviceIds.push(device.id);
              } else {
                found.set(info.personId, {
                  key: info.personId,
                  ...info,
                  deviceIds: [device.id],
                });
              }
            }
          }
        } catch {
          // Device unreachable — skip silently
        }
      }

      setScanProgress({ current: devices.length, total: devices.length, deviceId: '' });
      const results = Array.from(found.values()).sort((a, b) => {
        // active first, then expired, then exited, then unknown
        const order = { active: 0, expired: 1, exited: 2, unknown: 3 };
        return (order[a.visitorState as keyof typeof order] ?? 3) - (order[b.visitorState as keyof typeof order] ?? 3);
      });

      setEntries(results);
      setScanState('done');

      if (results.length === 0) {
        toast('Nenhum visitante encontrado nos dispositivos');
      } else {
        toast.success(`${results.length} visitante${results.length !== 1 ? 's' : ''} encontrado${results.length !== 1 ? 's' : ''} nos dispositivos`);
      }
    } catch (err) {
      setScanError(err instanceof Error ? err.message : 'Erro desconhecido');
      setScanState('idle');
      toast.error('Falha ao escanear dispositivos');
      console.error('Device audit scan error:', err);
    }
  }, [customerId]);

  // ── Selection ─────────────────────────────────────────────────────────────

  const handleToggle = useCallback((key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedKeys(
      selectedKeys.size === entries.length ? new Set() : new Set(entries.map(e => e.key))
    );
  }, [entries, selectedKeys.size]);

  // ── Revoke ────────────────────────────────────────────────────────────────

  const buildExpiredVisitor = (entry: DeviceAuditEntry): ExpiredVisitor => ({
    id: entry.personId,
    name: entry.name,
    photo: entry.photo,
    unit: 'N/A',
    personId: entry.personId,
    roleId: entry.roleId || '',
    roleType: entry.roleType as 'visitor' | 'service_provider',
    statusType: 'expired',
    expiredAt: new Date().toISOString(),
    expiredDaysAgo: 0,
    devicePersonId: entry.devicePersonId,
    devices: entry.deviceIds, // only remove from the specific devices where found
  });

  const runRevoke = useCallback(async (toRevoke: DeviceAuditEntry[]) => {
    if (toRevoke.length === 0) return;

    setIsRemoving(true);
    setShowProgress(true);
    setRemovalProgress({ current: 0, total: toRevoke.length, name: '' });

    try {
      const visitors = toRevoke.map(buildExpiredVisitor);
      const results = await expiredVisitorDeviceService.removeBatchFromDevices(
        customerId,
        visitors,
        (current, total, name) => setRemovalProgress({ current, total, name })
      );

      let ok = 0; let fail = 0;
      results.forEach(r => r.forEach(x => (x.success ? ok++ : fail++)));

      if (fail === 0) toast.success(`${toRevoke.length} visitante${toRevoke.length !== 1 ? 's' : ''} revogado${toRevoke.length !== 1 ? 's' : ''}`);
      else toast.error(`Parcial: ${ok} OK, ${fail} falhou`);

      // Remove revoked entries from the list
      const revokedKeys = new Set(toRevoke.map(e => e.key));
      setEntries(prev => prev.filter(e => !revokedKeys.has(e.key)));
      setSelectedKeys(new Set());
    } catch (err) {
      toast.error('Falha ao revogar visitantes');
      console.error('Revoke error:', err);
    } finally {
      setIsRemoving(false);
      setTimeout(() => setShowProgress(false), 2000);
    }
  }, [customerId]);

  const handleRevogarTodos = useCallback(async () => {
    if (entries.length === 0) return;
    const confirmed = await confirm({
      message: `Revogar TODOS os ${entries.length} visitantes encontrados nos dispositivos?\n\nEsta ação não pode ser desfeita.`,
      variant: 'danger',
      confirmLabel: 'Revogar',
    });
    if (!confirmed) return;
    await runRevoke(entries);
  }, [entries, runRevoke, confirm]);

  const handleRevogarSelecionados = useCallback(async () => {
    const toRevoke = entries.filter(e => selectedKeys.has(e.key));
    if (toRevoke.length === 0) { toast.error('Nenhum visitante selecionado'); return; }
    const confirmed = await confirm({
      message: `Revogar ${toRevoke.length} visitante${toRevoke.length !== 1 ? 's' : ''} selecionado${toRevoke.length !== 1 ? 's' : ''} dos dispositivos?\n\nEsta ação não pode ser desfeita.`,
      variant: 'danger',
      confirmLabel: 'Revogar',
    });
    if (!confirmed) return;
    await runRevoke(toRevoke);
  }, [entries, selectedKeys, runRevoke, confirm]);

  // ── Derived ───────────────────────────────────────────────────────────────

  const activeCount = useMemo(() => entries.filter(e => e.visitorState === 'active').length, [entries]);
  const expiredCount = useMemo(() => entries.filter(e => e.visitorState === 'expired' || e.visitorState === 'exited').length, [entries]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full bg-gray-950">

      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-800 flex-wrap">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-gray-300 hover:text-white
                     bg-gray-900 hover:bg-gray-800 rounded transition-colors flex-shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </button>

        <div className="flex items-center gap-2 flex-shrink-0">
          <ScanLine className="w-5 h-5 text-blue-400" />
          <h1 className="text-base font-semibold text-white">Auditoria de Dispositivos</h1>
        </div>

        {scanState === 'done' && entries.length > 0 && (
          <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-900 rounded-full text-xs text-gray-400 flex-shrink-0">
            <Wifi className="w-3.5 h-3.5 text-blue-400" />
            {activeCount > 0 && <span className="text-green-400">{activeCount} ativo{activeCount !== 1 ? 's' : ''}</span>}
            {activeCount > 0 && expiredCount > 0 && <span className="text-gray-600">·</span>}
            {expiredCount > 0 && <span className="text-red-400">{expiredCount} expirado{expiredCount !== 1 ? 's' : ''}</span>}
          </span>
        )}

        <div className="flex-1" />

        <div className="flex items-center gap-2">
          {/* Scan button */}
          <button
            onClick={runScan}
            disabled={scanState === 'scanning' || isRemoving}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                       text-blue-300 hover:text-blue-100 bg-blue-950/50 hover:bg-blue-950
                       border border-blue-900/50 rounded transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {scanState === 'scanning' ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                {scanProgress.total > 0
                  ? `Escaneando ${scanProgress.current}/${scanProgress.total}…`
                  : 'Escaneando…'}
              </>
            ) : scanState === 'done' ? (
              <>
                <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
                Reescanear
              </>
            ) : (
              <>
                <ScanLine className="w-3.5 h-3.5" />
                Escanear Dispositivos
              </>
            )}
          </button>

          {/* Revogar Todos */}
          {entries.length > 0 && (
            <button
              onClick={handleRevogarTodos}
              disabled={isRemoving || scanState === 'scanning'}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold
                         bg-red-700 hover:bg-red-600 text-white rounded transition-colors
                         disabled:bg-gray-700 disabled:cursor-not-allowed"
            >
              <ShieldOff className="w-3.5 h-3.5" />
              Revogar Todos ({entries.length})
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions bar */}
      {entries.length > 0 && (
        <div className="flex items-center justify-between px-4 py-2 bg-gray-900/20 border-b border-gray-800/60">
          <button
            onClick={handleSelectAll}
            className="flex items-center gap-2 text-xs text-gray-400 hover:text-gray-200 transition-colors"
          >
            {selectedKeys.size === entries.length ? (
              <CheckSquare className="w-4 h-4 text-red-500" />
            ) : (
              <Square className="w-4 h-4" />
            )}
            {selectedKeys.size === entries.length ? 'Desmarcar todos' : 'Selecionar todos'}
            {selectedKeys.size > 0 && selectedKeys.size !== entries.length && (
              <span className="text-gray-600 ml-1">({selectedKeys.size} selecionado{selectedKeys.size !== 1 ? 's' : ''})</span>
            )}
          </button>

          {selectedKeys.size > 0 && (
            <button
              onClick={handleRevogarSelecionados}
              disabled={isRemoving}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-700 hover:bg-red-600
                         disabled:bg-gray-700 disabled:cursor-not-allowed
                         text-white text-xs font-medium rounded transition-colors"
            >
              <ShieldOff className="w-3.5 h-3.5" />
              Revogar selecionados ({selectedKeys.size})
            </button>
          )}
        </div>
      )}

      {/* Column headers */}
      {entries.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-1.5 border-b border-gray-800/40 bg-gray-900/10">
          <div className="w-4 flex-shrink-0" />
          <div className="w-9 flex-shrink-0" />
          <span className="flex-1 text-[10px] font-medium text-gray-600 uppercase tracking-wider">Nome</span>
          <span className="flex-shrink-0 text-[10px] font-medium text-gray-600 uppercase tracking-wider">Dispositivos</span>
          <span className="flex-shrink-0 text-[10px] font-medium text-gray-600 uppercase tracking-wider">Status</span>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto">

        {/* Idle */}
        {scanState === 'idle' && !scanError && (
          <div className="flex flex-col items-center justify-center h-full gap-4 text-center px-8">
            <ScanLine className="w-14 h-14 text-gray-800" />
            <div>
              <p className="text-gray-400 text-sm font-medium">Auditoria de Visitantes nos Dispositivos</p>
              <p className="text-gray-600 text-xs mt-1">
                Escaneia todos os dispositivos fisicamente para encontrar visitantes ainda registrados —
                independente do status no banco de dados.
              </p>
            </div>
            <button
              onClick={runScan}
              className="flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-600 text-white text-sm rounded transition-colors"
            >
              <ScanLine className="w-4 h-4" />
              Iniciar Escaneamento
            </button>
          </div>
        )}

        {/* Error */}
        {scanError && (
          <div className="flex items-center justify-center h-48">
            <div className="text-center">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
              <p className="text-red-400 text-sm">{scanError}</p>
              <button onClick={runScan} className="mt-2 text-xs text-gray-500 hover:text-white underline">
                Tentar novamente
              </button>
            </div>
          </div>
        )}

        {/* Scanning */}
        {scanState === 'scanning' && (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
            <p className="text-gray-400 text-sm">
              {scanProgress.total > 0
                ? `Escaneando dispositivo ${scanProgress.current + 1} de ${scanProgress.total}…`
                : 'Conectando aos dispositivos…'}
            </p>
            {scanProgress.deviceId && (
              <p className="text-gray-600 text-xs font-mono">{scanProgress.deviceId}</p>
            )}
          </div>
        )}

        {/* Empty after scan */}
        {scanState === 'done' && entries.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <CheckCircle2 className="w-12 h-12 text-green-700" />
            <p className="text-gray-400 text-sm">Nenhum visitante encontrado nos dispositivos</p>
            <button onClick={runScan} className="text-xs text-gray-600 hover:text-white underline">
              Reescanear
            </button>
          </div>
        )}

        {/* Results */}
        {scanState === 'done' && entries.length > 0 && (
          <div>
            {entries.map(entry => (
              <AuditRow
                key={entry.key}
                entry={entry}
                isSelected={selectedKeys.has(entry.key)}
                onToggle={handleToggle}
                onPersonClick={onPersonClick}
              />
            ))}
          </div>
        )}
      </div>

      {/* Progress Modal */}
      <DeviceRemovalProgress
        isOpen={showProgress}
        currentIndex={removalProgress.current}
        totalCount={removalProgress.total}
        currentVisitorName={removalProgress.name}
        onClose={() => setShowProgress(false)}
      />
    </div>
  );
};
